import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  updatePassword,
  type SignInOutput,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import i18n from "@/i18n";
import { configureAmplify } from "./amplify-config";

configureAmplify();

export interface AuthUser {
  username: string;
  email: string;
  name?: string;
  /**
   * Grupos de Cognito del usuario (claim `cognito:groups` del id token).
   * Fuente de verdad del rol para el gating de UX (RBAC). Vacío si el usuario
   * no pertenece a ningún grupo. El enforcement real vive en el autorizador
   * del gateway; esto solo filtra nav y protege rutas en el frontend.
   */
  groups: string[];
}

/**
 * Resultado de signIn:
 * - "SIGNED_IN": flujo completo, hay sesión
 * - "NEW_PASSWORD_REQUIRED": el usuario debe cambiar su contraseña (force change)
 * - "RESET_REQUIRED": admin marcó la cuenta para reset
 */
export type SignInResult =
  | { status: "SIGNED_IN" }
  | { status: "NEW_PASSWORD_REQUIRED" }
  | { status: "RESET_REQUIRED" };

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<void>;
  /** Cambio de contraseña del usuario ya autenticado (requiere la actual). */
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

async function loadUser(): Promise<AuthUser | null> {
  try {
    const current = await getCurrentUser();
    const attrs = await fetchUserAttributes();
    const session = await fetchAuthSession();
    const rawGroups = session.tokens?.idToken?.payload["cognito:groups"];
    const groups = Array.isArray(rawGroups)
      ? rawGroups.filter((g): g is string => typeof g === "string")
      : [];
    return {
      username: current.username,
      email: attrs.email ?? current.signInDetails?.loginId ?? "",
      name: attrs.name,
      groups,
    };
  } catch {
    return null;
  }
}

function mapSignInOutput(out: SignInOutput): SignInResult {
  if (out.isSignedIn) return { status: "SIGNED_IN" };
  switch (out.nextStep.signInStep) {
    case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
      return { status: "NEW_PASSWORD_REQUIRED" };
    case "RESET_PASSWORD":
      return { status: "RESET_REQUIRED" };
    default:
      // El paso interno de Cognito identifica el flujo no soportado (MFA y demás), pero no
      // le dice nada al operador: va al log y la pantalla recibe el mensaje traducido.
      console.error(
        `[auth] Unsupported sign-in step: ${out.nextStep.signInStep}`,
      );
      throw new Error(i18n.t("auth.errors.unsupportedSignInStep"));
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    const u = await loadUser();
    setUser(u);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await loadUser();
      if (!cancelled) {
        setUser(u);
        setIsLoading(false);
      }
    })();

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
        case "tokenRefresh":
          void refresh();
          break;
        case "signedOut":
        case "tokenRefresh_failure":
          setUser(null);
          break;
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [refresh]);

  const signIn = useCallback<AuthState["signIn"]>(async (email, password) => {
    const out = await amplifySignIn({
      username: email,
      password,
      options: { authFlowType: "USER_PASSWORD_AUTH" },
    });
    const result = mapSignInOutput(out);
    if (result.status === "SIGNED_IN") await refresh();
    return result;
  }, [refresh]);

  const completeNewPassword = useCallback<
    AuthState["completeNewPassword"]
  >(async (newPassword) => {
    const out = await confirmSignIn({ challengeResponse: newPassword });
    if (!out.isSignedIn) {
      console.error(
        `[auth] confirmSignIn no completó la sesión; paso: ${out.nextStep.signInStep}`,
      );
      throw new Error(i18n.t("auth.errors.newPasswordIncomplete"));
    }
    await refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    await amplifySignOut();
    setUser(null);
  }, []);

  const requestPasswordReset = useCallback<
    AuthState["requestPasswordReset"]
  >(async (email) => {
    await resetPassword({ username: email });
  }, []);

  const confirmPasswordReset = useCallback<
    AuthState["confirmPasswordReset"]
  >(async (email, code, newPassword) => {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  }, []);

  const changePassword = useCallback<AuthState["changePassword"]>(
    async (oldPassword, newPassword) => {
      await updatePassword({ oldPassword, newPassword });
    },
    [],
  );

  const value = useMemo<AuthState>(
    () => ({
      isAuthenticated: user !== null,
      isLoading,
      user,
      signIn,
      completeNewPassword,
      signOut,
      requestPasswordReset,
      confirmPasswordReset,
      changePassword,
      refresh,
    }),
    [
      user,
      isLoading,
      signIn,
      completeNewPassword,
      signOut,
      requestPasswordReset,
      confirmPasswordReset,
      changePassword,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/**
 * Helper para apiClient — devuelve el id token de la sesión actual o null.
 * Amplify maneja refresh automáticamente al llamar a fetchAuthSession.
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}
